import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Link } from '@tanstack/react-router';
import { Users, GraduationCap, Book } from "lucide-react"
import { Main } from '@/components/layout/main'
import { Header } from '@/components/layout/header'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { TopNav } from '@/components/layout/top-nav'
import { useEffect, useState } from 'react'
import apiClient from '@/lib/axios'

interface Bolum {
    _id: string;
    ad: string;
    ogrenciSayisi: number;
    dersSayisi: number;
    egitmenId?: {
        _id: string;
        adSoyad: string;
        unvan: string;
    };
}
const topNav = [
    {
        title: "Ana Sayfa",
        href: "/",
        isActive: false,
    },
    {
        title: "Bölümler",
        href: "/bolumler",
        isActive: true,
    },
    {
        title: "Eğitmenler",
        href: "/egitmenler",
        isActive: false,
    },
]

export default function BolumlerPage() {
    const [bolumler, setBolumler] = useState<Bolum[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchBolumler = async () => {
            try {
                const response = await apiClient.get('/bolum');
                setBolumler(response.data);
            } catch (err) {
                setError('Bölümler yüklenirken bir hata oluştu.');
            } finally {
                setLoading(false);
            }
        };

        fetchBolumler();
    }, []);

    if (loading) {
        return (
            <>
                <Header>
                    <TopNav links={topNav} />
                    <div className='ml-auto flex items-center space-x-4'>
                        <Search />
                        <ThemeSwitch />
                        <ProfileDropdown />
                    </div>
                </Header>
                <Main className="peer-[.header-fixed]/header:mt-16 px-4 py-6">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <Skeleton className="h-8 w-48 mb-2" />
                            <Skeleton className="h-4 w-64" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[...Array(6)].map((_, i) => (
                            <Card key={i}>
                                <CardHeader>
                                    <Skeleton className="h-6 w-3/4 mb-2" />
                                    <Skeleton className="h-4 w-full" />
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        <Skeleton className="h-4 w-2/3" />
                                        <Skeleton className="h-4 w-2/3" />
                                    </div>
                                </CardContent>
                                <CardFooter>
                                    <Skeleton className="h-10 w-full" />
                                </CardFooter>
                            </Card>
                        ))}
                    </div>
                </Main>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header>
                    <TopNav links={topNav} />
                    <div className='ml-auto flex items-center space-x-4'>
                        <Search />
                        <ThemeSwitch />
                        <ProfileDropdown />
                    </div>
                </Header>
                <Main className="peer-[.header-fixed]/header:mt-16 px-4 py-6">
                    <div className="text-center py-8 text-red-500">{error}</div>
                </Main>
            </>
        );
    }

    return (
        <>
            <Header>
                <TopNav links={topNav} />
                <div className='ml-auto flex items-center space-x-4'>
                    <Search />
                    <ThemeSwitch />
                    <ProfileDropdown />
                </div>
            </Header>

            <Main className="peer-[.header-fixed]/header:mt-16 px-4 py-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">Bölümler</h1>
                        <p className="text-muted-foreground">Yüksekokulumuzda bulunan tüm bölümlerin listesi</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {bolumler.map((bolum) => (
                        <Card key={bolum._id} className="relative">
                            <CardHeader>
                                <div className="flex justify-between items-start">
                                    <CardTitle className="text-xl">{bolum.ad}</CardTitle>
                                </div>
                                {bolum.egitmenId && (
                                    <CardDescription>{bolum.egitmenId.unvan} {bolum.egitmenId.adSoyad}</CardDescription>
                                )}
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center space-x-2">
                                    <Users className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">
                                        Kayıt Yapan {bolum.ogrenciSayisi} Öğrenci
                                    </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Book className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">
                                        Toplam {bolum.dersSayisi} Ders
                                    </span>
                                </div>
                            </CardContent>

                            <CardFooter>
                                <Link to="/bolumler/bolumDetay/$bolumId" params={{ bolumId: bolum._id }} className="w-full">
                                    <Button variant="outline" className="w-full">
                                        <GraduationCap className="mr-2 h-4 w-4" />
                                        Detayları Görüntüle
                                    </Button>
                                </Link>
                            </CardFooter>
                        </Card>
                    ))}
                </div>
            </Main>
        </>
    );
}
